import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  TextField, 
  Typography,
  Box,
  Paper,
  Divider,
  Tab,
  Tabs
} from '@mui/material';
import axios from '../../utils/axiosConfig';
import ImageUpload from '../../components/ImageUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import ClubLogo from '../../components/ClubLogo';

interface Club {
  _id: string;
  name: string;
  address: {
    street: string;
    postalCode: string;
    city: string;
  };
  hasLogo?: boolean;
  contactPerson: {
    _id: string;
    name: string;
    email: string;
  };
}

interface ClubEditDialogProps {
  open: boolean;
  onClose: () => void;
  club: Club | null;
  onClubUpdated: (club: Club) => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`club-edit-tabpanel-${index}`}
      aria-labelledby={`club-edit-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ClubEditDialog: React.FC<ClubEditDialogProps> = ({
  open,
  onClose,
  club,
  onClubUpdated
}) => {
  const [formData, setFormData] = useState<{
    name: string;
    address: {
      street: string;
      postalCode: string;
      city: string;
    };
  }>({
    name: '',
    address: {
      street: '',
      postalCode: '',
      city: ''
    }
  });

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [logoUpdated, setLogoUpdated] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    if (club && open) {
      setFormData({
        name: club.name,
        address: {
          street: club.address.street,
          postalCode: club.address.postalCode,
          city: club.address.city
        }
      });
      setError(null);
      setTabValue(0); // Reset to first tab when opening
    }
  }, [club, open]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      name: e.target.value
    }));
  };

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      address: {
        ...prev.address,
        [name]: value
      }
    }));
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleSubmit = async () => {
    if (!club) return;
    
    setLoading(true);
    try {
      const response = await axios.put(`/api/clubs/${club._id}`, {
        name: formData.name,
        address: formData.address
      });
      
      // Update the club with the returned data
      onClubUpdated({
        ...club,
        name: formData.name,
        address: formData.address,
        hasLogo: logoUpdated ? true : club.hasLogo
      });
      
      onClose();
    } catch (error) {      
      setError('Error saving club. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoSuccess = () => {
    setLogoUpdated(true);
    if (club) {
      // Update local state to reflect logo change
      club.hasLogo = true;
    }
  };

  const handleLogoDelete = async () => {
    if (!club) return;
    
    try {
      await axios.delete(`/api/images/Club/${club._id}`);
      setLogoUpdated(true);
      if (club) {
        // Update local state to reflect logo removal
        club.hasLogo = false;
      }
    } catch (error) {      
      setError('Error deleting logo. Please try again later.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Verein bearbeiten
      </DialogTitle>
      <DialogContent>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="club edit tabs">
            <Tab label="Grunddaten" id="club-edit-tab-0" />
            <Tab label="Logo" id="club-edit-tab-1" />
          </Tabs>
        </Box>
        
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              name="name"
              label="Vereinsname"
              value={formData.name}
              onChange={handleNameChange}
              fullWidth
              required
            />

            <Typography variant="subtitle1" sx={{ mt: 1 }}>Adresse</Typography>
            
            <TextField
              name="street"
              label="Straße"
              value={formData.address.street}
              onChange={handleAddressChange}
              fullWidth
            />
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                name="postalCode"
                label="PLZ"
                value={formData.address.postalCode}
                onChange={handleAddressChange}
                fullWidth
              />
              
              <TextField
                name="city"
                label="Stadt"
                value={formData.address.city}
                onChange={handleAddressChange}
                fullWidth
              />
            </Box>

            {club && club.contactPerson && (
              <Paper elevation={0} sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                <Typography variant="subtitle1">Ansprechpartner</Typography>
                <Typography variant="body1" sx={{ mt: 1 }}>
                  {club.contactPerson.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {club.contactPerson.email}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                  Die Kontaktperson kann nicht bearbeitet werden.
                </Typography>
              </Paper>
            )}
          </Box>
        </TabPanel>
        
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            {club && club.hasLogo ? (
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{ mb: 2 }}>
                  <ClubLogo 
                    clubId={club._id} 
                    hasLogo={club.hasLogo} 
                    size="large" 
                  />
                </Box>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleLogoDelete}
                >
                  Logo löschen
                </Button>
              </Box>
            ) : (
              <Box sx={{ width: '100%' }}>
                <Typography variant="body2" gutterBottom>
                  Kein Logo vorhanden. Lade bitte ein Logo hoch (JPG oder PNG, max. 5MB, max. 1024x1024 Pixel).
                </Typography>
                {club && (
                  <ImageUpload
                    entityId={club._id}
                    entityType="Club"
                    onSuccess={handleLogoSuccess}
                    label="Vereinslogo"
                  />
                )}
              </Box>
            )}
          </Box>
        </TabPanel>

        {error && (
          <Typography color="error" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Abbrechen
        </Button>
        <Button 
          onClick={handleSubmit} 
          color="primary" 
          variant="contained"
          disabled={loading}
        >
          {loading ? 'Wird gespeichert...' : 'Speichern'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClubEditDialog;